import { format, addDays, subDays, parse, isAfter, isBefore, addHours } from 'date-fns';

/**
 * サーバー送信用に日付時刻をフォーマットする
 * @param date - フォーマットするDateオブジェクト
 * @returns 'YYYY-MM-DDTHH:mm:ss' 形式の文字列
 */
export const formatDateTimeForServer = (date: Date): string => {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
};

/**
 * 指定された時刻が深夜営業時間内（0:00～営業終了時間）かどうかをチェックする
 * @param timeStr 時刻文字列（HH:MM形式）
 * @param endTimeStr 営業終了時間（HH:MM形式）
 * @returns 深夜営業時間内の場合はtrue
 */
const isAfterMidnightBeforeClosing = (timeStr: string, endTimeStr: string): boolean => {
  if (!timeStr || !endTimeStr) return false;
  const currentTime = parse(timeStr, 'HH:mm', new Date());
  const endTime = parse(endTimeStr, 'HH:mm', new Date());
  return isBefore(currentTime, endTime);
};

/**
 * 日付選択コンポーネント用に調整された日付を返す
 * 深夜0時から営業終了時間までの間は前日の日付を返す
 * @param businessHours - 営業時間のオブジェクト { start: string, end: string }
 * @param date - 調整する日付（省略時は現在時刻）
 * @returns 調整された日付
 */
export const getAdjustedDate = (
  businessHours: { start: string; end: string } | null,
  date: Date = new Date()
): Date => {
  if (!businessHours || !businessHours.start || !businessHours.end) {
    console.warn('[dateUtils] 営業時間が未設定のため、日付調整をスキップします。');
    return date;
  }

  const { start, end } = businessHours;
  const startTime = parse(start, 'HH:mm', new Date());
  const endTime = parse(end, 'HH:mm', new Date());

  // 日付をまたぐ深夜営業でない場合は、調整は不要
  if (isBefore(startTime, endTime)) {
    return date;
  }
  
  const currentTimeStr = format(date, 'HH:mm');

  // 現在時刻が営業終了時刻より前の場合（深夜営業中）、日付を1日前にする
  if (isAfterMidnightBeforeClosing(currentTimeStr, end)) {
    return subDays(date, 1);
  }

  return date;
};

/**
 * 現在の業務日付を取得する
 * @param businessHours - 営業時間のオブジェクト { start: string, end: string }
 * @returns 現在の業務日付
 */
export const getCurrentBusinessDate = (
  businessHours: { start: string; end: string } | null
): Date => {
  return getAdjustedDate(businessHours, new Date());
};

/**
 * 時刻文字列（HH:mm）と日付からDateオブジェクトを生成する
 * @param date -基準となる日付
 * @param timeStr - 時刻文字列 (HH:mm)
 * @returns 生成されたDateオブジェクト
 */
export const combineDateAndTime = (date: Date, timeStr: string): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0); // 秒とミリ秒を0に設定
  return newDate;
};